import webtmsBackend from './webtmsBackend';

const MOCK_STORAGE_KEY = 'tms.advancedBilling.mockWorkflow.v1';

const createInitialWorkflow = () => ({
    dayState: 'not-started',
    elapsedSeconds: 0,
    endedAt: null,
    startedAt: null,
    breaks: [],
    jobs: [
        {
            id: 'A913601859-02A',
            address: '14 Shoreditch High St, Washington',
            billingCode: 'HVAC',
            rateLabel: 'Regular Rates',
            rateType: 'regular',
            status: 'pending',
            units: 4,
            billingStartedAt: null,
            arrivedAt: null,
            respondedAt: null,
            completedAt: null,
        },
        {
            id: 'A913601860-02A',
            address: '8 Cedar Close SE1',
            billingCode: 'PLUMBING',
            rateLabel: 'Regular Rates',
            rateType: 'regular',
            status: 'pending',
            units: 1,
            billingStartedAt: null,
            arrivedAt: null,
            respondedAt: null,
            completedAt: null,
        },
        {
            id: 'A913601861-02A',
            address: 'Tower Road SW1',
            billingCode: 'GAS',
            rateLabel: 'Afterhour Rates',
            rateType: 'afterhours',
            status: 'pending',
            units: 2,
            billingStartedAt: null,
            arrivedAt: null,
            respondedAt: null,
            completedAt: null,
        },
        {
            id: 'A913601862-02A',
            address: 'Flat 12A, Bermondsey SE1',
            billingCode: 'INSPECTION',
            rateLabel: 'Afterhour Rates',
            rateType: 'afterhours',
            status: 'pending',
            units: 10,
            billingStartedAt: null,
            arrivedAt: null,
            respondedAt: null,
            completedAt: null,
        },
    ],
});

const cloneWorkflow = workflow => JSON.parse(JSON.stringify(workflow));

const normalizeWorkflowIdentifier = value => String(value || '')
    .replace(/[{}]/g, '')
    .toLowerCase();

const getTicketResponseJobId = ticket => normalizeWorkflowIdentifier(ticket?.TR_rowid);

const findTicketResponseJob = (workflow, ticket) => {
    const ticketResponseJobId = getTicketResponseJobId(ticket);
    if(!ticketResponseJobId) {
        return undefined;
    }
    return workflow?.jobs?.find(job => normalizeWorkflowIdentifier(
        job.ticketResponseId || job.id
    ) === ticketResponseJobId);
};

const getTicketResponseBillingRecords = (ticketDetails, ticket) => {
    const responseId = getTicketResponseJobId(ticket);
    const records = ticketDetails?.Ticket_ResponsesBilling;
    if(!responseId || !Array.isArray(records)) {
        return [];
    }
    return records.filter(record => normalizeWorkflowIdentifier(
        record.TR_rowid
    ) === responseId);
};

const normalizeBillingRecord = record => ({
    id: normalizeWorkflowIdentifier(record.rowid),
    code: String(record.code || '').trim(),
    amount: record.amount,
    unitMinutes: Number(record.minutes),
});

const createJobFromTicketResponse = (ticket, billingRecords = []) => {
    const ticketResponseId = getTicketResponseJobId(ticket);
    if(!ticketResponseId) {
        throw new Error('A ticket response row is required for Advanced Billing.');
    }

    const ticketNumber = ticket.ticketRevision
        || [ticket.ticket, ticket.revision].filter(Boolean).join('-');
    const billingCodes = billingRecords
        .map(normalizeBillingRecord)
        .filter(record => record.code);
    const billingCode = billingCodes.map(record => record.code).join(', ')
        || 'Not assigned';

    return {
        id: ticketResponseId,
        ticketResponseId,
        ticketId: normalizeWorkflowIdentifier(ticket.tid),
        ticket: ticket.ticket,
        revision: ticket.revision,
        ticketNumber,
        memberCode: ticket.TR_member_code || '',
        address: ticket.address || ticket.street || 'Address not available',
        billingCodes,
        billingConfigurationLoaded: true,
        billingCode,
        billingCodeId: billingCodes.length === 1 ? billingCodes[0].id : undefined,
        rateLabel: 'Not assigned',
        ratePeriodId: undefined,
        rateType: '',
        status: 'pending',
        units: 0,
        allocations: [],
        billingStartedAt: null,
        arrivedAt: null,
        respondedAt: null,
        completedAt: null,
    };
};

const createBlankWorkflow = () => ({
    workDate: new Date().toISOString().slice(0, 10),
    dayState: 'not-started',
    elapsedSeconds: 0,
    endedAt: null,
    startedAt: null,
    breaks: [],
    jobs: [],
});

const getStorage = () => {
    try {
        return window.localStorage;
    } catch(error) {
        return null;
    }
};

const mockAdvancedBillingApi = {
    async getWorkflow() {
        const storedWorkflow = getStorage()?.getItem(MOCK_STORAGE_KEY);
        return storedWorkflow
            ? JSON.parse(storedWorkflow)
            : createInitialWorkflow();
    },
    async saveWorkflow(workflow) {
        getStorage()?.setItem(MOCK_STORAGE_KEY, JSON.stringify(workflow));
        return cloneWorkflow(workflow);
    },
    async submitWorkflow(workflow) {
        getStorage()?.setItem(MOCK_STORAGE_KEY, JSON.stringify(workflow));
        return cloneWorkflow(workflow);
    },
    async resetWorkflow() {
        const workflow = createBlankWorkflow();
        getStorage()?.setItem(MOCK_STORAGE_KEY, JSON.stringify(workflow));
        return cloneWorkflow(workflow);
    },
};

const parseStoredWorkflow = storedWorkflow => {
    if(storedWorkflow === false
        || storedWorkflow === null
        || storedWorkflow === undefined
        || (typeof storedWorkflow === 'string' && storedWorkflow.trim() === '')) {
        return createBlankWorkflow();
    }

    let workflow;
    try {
        workflow = typeof storedWorkflow === 'string'
            ? JSON.parse(storedWorkflow)
            : storedWorkflow;
    } catch(error) {
        throw new Error('The stored Advanced Billing workflow is not valid JSON.');
    }

    if(workflow
        && typeof workflow === 'object'
        && !Array.isArray(workflow)
        && Object.keys(workflow).length === 0) {
        return createBlankWorkflow();
    }
    if(!workflow || typeof workflow !== 'object' || !Array.isArray(workflow.jobs)) {
        throw new Error('The stored Advanced Billing workflow has an invalid shape.');
    }
    return cloneWorkflow(workflow);
};

const webtmsLiveAdvancedBillingApi = {
    async getWorkflow() {
        return parseStoredWorkflow(await webtmsBackend.wtmBillingWorkflowGet());
    },
    async saveWorkflow(workflow) {
        const result = await webtmsBackend.wtmBillingWorkflowSet(workflow);
        if(!result || result.err || result.success === false) {
            throw new Error(
                result?.err || 'The Advanced Billing workflow could not be saved.'
            );
        }
        return result.workflow
            ? parseStoredWorkflow(result.workflow)
            : cloneWorkflow(workflow);
    },
    async submitWorkflow(workflow) {
        const result = await webtmsBackend.wtmBillingWorkflowSubmit(
            workflow.workDate,
            workflow
        );
        if(result !== true) {
            throw new Error(
                result || 'The Advanced Billing workflow could not be submitted.'
            );
        }
        return cloneWorkflow(workflow);
    },
    async resetWorkflow(workDate) {
        const datedOption = `BILLING_WORKFLOW_${String(
            workDate || createBlankWorkflow().workDate
        ).replace(/-/g, '')}`;
        await Promise.all([
            webtmsBackend.deleteUserOption('BILLING_WORKFLOW'),
            webtmsBackend.deleteUserOption(datedOption),
        ]);
        return this.saveWorkflow(createBlankWorkflow());
    },
};

let configuredLiveApi = webtmsLiveAdvancedBillingApi;

const getConfiguredLiveApi = () => {
    if(!configuredLiveApi) {
        throw new Error('The live Advanced Billing API adapter has not been configured.');
    }
    return configuredLiveApi;
};

const liveAdvancedBillingApi = {
    async getWorkflow() {
        return getConfiguredLiveApi().getWorkflow();
    },
    async saveWorkflow(workflow) {
        return getConfiguredLiveApi().saveWorkflow(workflow);
    },
    async submitWorkflow(workflow) {
        return getConfiguredLiveApi().submitWorkflow(workflow);
    },
    async resetWorkflow(workDate) {
        const api = getConfiguredLiveApi();
        if(typeof api.resetWorkflow !== 'function') {
            throw new Error(
                'The Advanced Billing API adapter does not support development resets.'
            );
        }
        return api.resetWorkflow(workDate);
    },
};

const adapters = {
    live: liveAdvancedBillingApi,
    mock: mockAdvancedBillingApi,
};

const resolveAdvancedBillingDataSource = configuredDataSource =>
    configuredDataSource === 'mock' ? 'mock' : 'live';

export const advancedBillingDataSource = resolveAdvancedBillingDataSource(
    process.env.VUE_APP_ADVANCED_BILLING_DATA_SOURCE
);

export const advancedBillingApi =
    adapters[advancedBillingDataSource];

export const configureAdvancedBillingLiveApi = api => {
    if(typeof api?.getWorkflow !== 'function'
        || typeof api?.saveWorkflow !== 'function'
        || typeof api?.submitWorkflow !== 'function') {
        throw new Error(
            'An Advanced Billing API adapter must implement getWorkflow, saveWorkflow, and submitWorkflow.'
        );
    }
    configuredLiveApi = api;
};

export {
    createJobFromTicketResponse,
    createBlankWorkflow,
    createInitialWorkflow,
    findTicketResponseJob,
    getTicketResponseBillingRecords,
    getTicketResponseJobId,
    liveAdvancedBillingApi,
    mockAdvancedBillingApi,
    normalizeWorkflowIdentifier,
    parseStoredWorkflow,
    resolveAdvancedBillingDataSource,
    webtmsLiveAdvancedBillingApi,
};
